import { IProduto } from "shared/interfaces/IProduto";
import { api } from "../api";
import { toCombinationPayload, type ProductCombinationInput } from "./combinacoes.api";

interface CriarProdutoDTO {
  uidDono: string;
  lojaId: string;
  lojaNome: string;
  sku?: string;
  name: string;
  categoriaId: string;
  categoriaNome: string;
  descricao?: string;
  caracteristicas: string[];
  price?: number;
  pricePromo?: number;
  estoque?: number;
  status: "Em Estoque" | "Fora de Estoque" | "Encomendado";
  gallery: string[];
  isActive?: boolean;
}

export async function criarProduto(data: CriarProdutoDTO, idempotencyKey: string): Promise<string> {
  try {
    const produtoAtivo: boolean = data.status === "Em Estoque";
    const payload = {
      storeId: data.lojaId,
      categoryId: data.categoriaId,
      name: data.name,
      description: data.descricao,
      sku: data.sku,
      price: data.price ?? 0,
      stock: parseInt(String(data.estoque ?? 0), 10),
      characteristics: data.caracteristicas,
      isActive: produtoAtivo,
    };

    const response = await api.post("/products", payload, {
      headers: { "Idempotency-Key": idempotencyKey },
    });

    return response.data.id;
  } catch (error) {
    throw error;
  }
}

export async function excluirProduto(produtoId: string) {
  await api.delete(`/products/${produtoId}`);
}

export async function buscarProdutoPorId(produtoId: string): Promise<IProduto | null> {
  try {
    const response = await api.get(`/v2/products/${produtoId}`);
    const produto = response.data;

    return {
      id: produto.id,
      uidDono: produto.ownerId,
      lojaId: produto.storeId,
      lojaNome: produto.storeName,
      sku: produto.sku,
      name: produto.name,
      categoriaId: produto.category?.id ?? produto.categoryId,
      categoriaNome: produto.category?.name ?? produto.categoryName,
      gallery: produto.gallery ?? [],
      descricao: produto.description,
      caracteristicas: produto.characteristics ?? [],
      price: produto.price,
      estoque: produto.stock,
      status: produto.status,
      isActive: produto.isActive,
      createdAt: produto.createdAt,
      priceMin: produto.priceMin,
      priceMax: produto.priceMax,
      hasVariations: Boolean(produto.hasVariations),
      variations: produto.variations ?? [],
      combinations: produto.combinations ?? [],
      installment: produto.installment ?? null,
    };
  } catch (error) {
    return null;
  }
}

export async function buscarMeuProdutoPorId(produtoId: string): Promise<IProduto | null> {
  try {
    const response = await api.get(`/products/mine/${produtoId}`);
    const produto = response.data;
    const galleryItems = produto.gallery ?? [];
    const gallery = galleryItems.map((img: any) => typeof img === "string" ? img : img.url);
    const galleryKeys = galleryItems
      .map((img: any) => (typeof img === "string" ? undefined : img.key))
      .filter((key: string | undefined): key is string => Boolean(key));

    return {
      id: produto.id,
      uidDono: produto.ownerId,
      lojaId: produto.storeId,
      lojaNome: produto.storeName,
      sku: produto.sku,
      name: produto.name,
      categoriaId: produto.category?.id ?? produto.categoryId,
      categoriaNome: produto.category?.name ?? produto.categoryName,
      gallery,
      galleryKeys,
      descricao: produto.description,
      caracteristicas: produto.characteristics ?? [],
      price: produto.price,
      estoque: produto.stock,
      status: produto.status,
      isActive: produto.isActive,
      createdAt: produto.createdAt,
    };
  } catch (error) {
    return null;
  }
}

export async function atualizarProduto(produtoId: string, data: Partial<CriarProdutoDTO>) {
  const payload: any = {};

  if (data.name !== undefined) payload.name = data.name;
  if (data.sku !== undefined) payload.sku = data.sku;
  if (data.descricao !== undefined) payload.description = data.descricao;
  if (data.estoque !== undefined) payload.stock = parseInt(String(data.estoque), 10);
  if (data.price !== undefined) payload.price = data.price;
  if (data.caracteristicas !== undefined) payload.characteristics = data.caracteristicas;
  if (data.categoriaId !== undefined) payload.categoryId = data.categoriaId;
  if (data.lojaId !== undefined) payload.storeId = data.lojaId;

  if (data.isActive !== undefined) {
    payload.isActive = data.isActive;
  } else if (data.status !== undefined) {
    payload.isActive = data.status === "Em Estoque";
  }

  if (data.gallery !== undefined) payload.gallery = data.gallery;

  try {
    await api.patch(`/products/${produtoId}`, payload);
  } catch (error) {
    throw error;
  }
}

export async function meusProdutos() {
  const response = await api.get("/products/mine");

  return response.data.map((produto: any) => {
    const galleryItems = produto.gallery ?? [];
    const gallery = galleryItems.map((img: any) => typeof img === "string" ? img : img.url);
    const galleryKeys = galleryItems
      .map((img: any) => (typeof img === "string" ? undefined : img.key))
      .filter((key: string | undefined): key is string => Boolean(key));

    return {
      ...produto,
      gallery,
      galleryKeys,
      estoque: produto.stock,
      descricao: produto.description,
      categoriaId: produto.category?.id ?? produto.categoryId,
      categoriaNome: produto.category?.name ?? produto.categoryName,
      isActive: produto.isActive,
    };
  });
}

interface ListarProdutosParams {
  page?: number;
  limit?: number;
  q?: string;
  variety?: boolean;
  generalization?: "masculino" | "feminino" | "infantil";
  sort?: "NEWEST" | "PRICE_ASC" | "PRICE_DESC" | "RELEVANCE";
}

interface ListarProdutosResponse {
  produtos: IProduto[];
  total: number;
  page: number;
  lastPage: number;
}

export async function listarProdutos(params?: ListarProdutosParams): Promise<ListarProdutosResponse> {
  try {
    const response = await api.get("/v2/products", { params });
    const produtos = response.data.data ?? [];
    const meta = response.data.meta ?? {};

    const produtosFormatados = produtos.map((produto: any) => ({
      ...produto,
      descricao: produto.description,
      estoque: produto.stock,
      categoriaId: produto.category?.id ?? produto.categoryId ?? "",
      categoriaNome: produto.category?.name ?? produto.categoryName ?? "",
      isActive: produto.isActive,
    }));

    return {
      produtos: produtosFormatados,
      total: Number(meta.total ?? produtosFormatados.length),
      page: Number(meta.page ?? params?.page ?? 1),
      lastPage: Number(meta.lastPage ?? 1),
    };
  } catch (error) {
    return {
      produtos: [],
      total: 0,
      page: 1,
      lastPage: 1,
    };
  }
}

export async function sincronizarCombinacoesProduto(
  produtoId: string,
  combinations: ProductCombinationInput[],
) {
  await api.put(`/v2/products/${produtoId}/combinations`, {
    combinations: toCombinationPayload(combinations),
  });
}